import { sumItems } from '@voll-intelligence/intelligence-ui-kit/checkout';
import type { CheckoutCardSlots } from '../../ui/card_slots.js';
import type { OneClickRecordKeys } from '../../ui/one_click/one_click_actions.js';
import { SummaryFoot } from '../../ui/summary_foot.js';
import {
  resolveGenUiLocale,
  useGenUiI18n,
  type GenUiTranslate,
} from '../../../../shared/ui/i18n.js';
import { MobilityCheckoutBlock, type MobilityCheckoutItem } from './checkout_block.js';
import { useCheckoutEnv } from '../../core/checkout_env.js';

/** O registro no vocabulário da corrida (#6699). */
const RECORDS: OneClickRecordKeys = {
  done: 'productCheckout.mobility.done',
  pendingApproval: {
    full: 'productCheckout.mobility.pendingApproval',
    short: 'productCheckout.mobility.pendingApprovalShort',
  },
};

/** O nome do escudo do total fora da política (#6699): a distância e, quando a
 *  política o diz, o limite — «Fora da política: 18 km, limite 15 km». */
function shieldLabel(
  items: readonly MobilityCheckoutItem[],
  t: GenUiTranslate,
  locale: string,
): string | undefined {
  const detail = items.find((item) => !item.detail.withinPolicy)?.detail;
  if (detail?.distanceKm === undefined) return undefined;
  // Uma casa: arredondar ao inteiro faria 15,4 km contra 15,2 km virar «15 km, limite 15 km».
  const fmt = (value: number) =>
    value.toLocaleString(resolveGenUiLocale(locale), { maximumFractionDigits: 1 });
  const km = fmt(detail.distanceKm);
  return detail.distanceLimitKm === undefined
    ? t('productCheckout.mobility.outOfPolicyKm', { km })
    : t('productCheckout.mobility.outOfPolicyDistance', {
        km,
        limit: fmt(detail.distanceLimitKm),
      });
}

/** O card da corrida: o bloco de cada corrida (o agente emite uma), a
 *  conferência do 1-Click e o rodapé com o total previsto — «· ida e volta»
 *  com volta — e a nota de que o valor final pode mudar. */
export function MobilityCheckoutCard({
  items,
  slots,
}: {
  readonly items: readonly MobilityCheckoutItem[];
  readonly slots: CheckoutCardSlots;
}) {
  const { t, locale } = useGenUiI18n();
  const { motive } = useCheckoutEnv().copy;
  const roundTrip = items.some((item) => item.detail.return);
  return (
    <>
      <div className="checkout__body">
        {items.map((item) => (
          <MobilityCheckoutBlock key={item.offerId} item={item} />
        ))}
      </div>
      {slots.review}
      <SummaryFoot
        valueKey={
          roundTrip
            ? 'productCheckout.mobility.totalEstimatedRoundTrip'
            : 'productCheckout.mobility.totalEstimated'
        }
        value={sumItems(items)}
        caption={t('productCheckout.mobility.estimateNote')}
        inPolicy={slots.flow.withinPolicy}
        shieldAriaKey="productCheckout.outOfPolicy"
        shieldTitleKey="productCheckout.outOfPolicy"
        shieldLabel={shieldLabel(items, t, locale)}
        actions={slots.actions('productCheckout.reserveMobility', motive.ride, RECORDS)}
      />
    </>
  );
}
